<template>
    <component
        :is="button.is"
        :class="classButton"
        class="button-round"
        v-bind="button.bind"
    >
        <Icon
            v-if="icon"
            :icon="icon"
        />

        <slot
            v-else-if="$slots['default']"
            name="default"
        ></slot>

        <span v-else>
            {{ label }}
        </span>
    </component>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'

import type { Icons } from '@/components/ui/icon'
import {
    type ButtonProps,
    useThButton,
} from '@/components/ui/buttons/useThButton'
import { useSlotText } from '@/components/ui/buttons/useSlotText'
import type { ButtonSize, ButtonType } from '.'

import Icon from '@/components/ui/icon/Icon.vue'

const props = withDefaults(
    defineProps<
        ButtonProps & {
            icon?: Icons
            label?: string
            type?: ButtonType
            size?: ButtonSize
        }
    >(),
    {
        icon: undefined,
        label: undefined,
        type: 'solid',
        size: 'medium',
    }
)

const emit = defineEmits<{
    click: [v: PointerEvent]
}>()

const slots = useSlots()
const defaultSlotContent = useSlotText(slots['default'])
const buttonLabel = computed(() => props.label || defaultSlotContent.value)

const button = useThButton(props, emit, buttonLabel)

const classButton = computed(() => ({
    disabled: props.disabled,
    'has-icon': !!props.icon,
    [props.type]: true,
    [props.size]: true,
}))
</script>

<style lang="scss" scoped>
.button-round {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 50px;
    border-radius: 100%;
    font-family: $effra-font-stack;
    font-weight: 400;
    letter-spacing: 0.02em;
    background-color: $blue;
    color: $stone-2;
    font-size: 1.375rem;
    cursor: pointer;
    text-align: center;

    &.disabled {
        background-color: $white;
        color: $grey-300;
        border-color: $white;
        cursor: default;
    }

    &.has-icon {
        padding-bottom: 0;
    }

    &.link {
        background-color: transparent;
        color: $slate;

        &.disabled {
            color: $grey-300;
        }
    }

    &.small {
        width: 40px;
        height: 40px;
        font-size: 1.25rem;
    }

    .icon {
        display: flex;
        height: 100%;
        width: 100%;
        justify-content: center;
        align-items: center;
        font-size: 1.5rem;
        line-height: 50px;
    }

    &.bordered {
        background: transparent;
        border: 1px solid $blue;
        color: $blue;
    }
}
</style>
